import { stripAmountFormatting } from "@/lib/formatters";
import {
  canonicalMonthToBill,
  CLOVER_SECURITY_AND_TRANSARMOR_FIELDS,
  CLOVER_SECURITY_AND_TRANSARMOR_DEFAULTS,
  etfCapError,
  FEE_OVERRIDE_FIELDS,
  FLEET_OVERRIDE_FIELDS,
  FNS_NUMBER_DIGITS,
  isValidFnsNumber,
  PRICING_TIERS,
  selectPricingTier,
  TRANSARMOR_TOKEN_TYPE_CODES,
} from "@/lib/pricingTiers";
import type {
  CloverSecurityAndTransarmorInput,
  FeeOverridesInput,
  FleetOverridesInput,
  IcPlusPricingOverridesInput,
} from "@/lib/types";
import type {
  CloverSecurityAndTransarmorForm,
  FeeOverridesForm,
  FleetOverridesForm,
  IcPlusPricingOverridesForm,
} from "./types";

// Why a non-blank override entry can't be used: "malformed" (Number() can't
// parse it -- e.g. "3.4.5", "1.2.3"), or "range" (parses, but negative / over
// the cap / not a whole number where one is required). `null` means blank or
// fine. The two cases get different operator-facing messages -- a malformed
// value is quoted back so the typo is obvious, an out-of-range one states the
// bound.
type OverrideProblem = "malformed" | "range";

function classifyOverride(
  raw: string,
  opts: { max: number; integer?: boolean },
): OverrideProblem | null {
  const t = stripAmountFormatting(raw);
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return "malformed";
  if (opts.integer && !Number.isInteger(n)) return "malformed";
  if (n < 0 || n > opts.max) return "range";
  return null;
}

// Inline validation message for one bad override field. Always names the field
// (there are ~20 override inputs across the admin sections and the roll-up
// banner would otherwise show identical lines); a malformed value is quoted
// back, an out-of-range one gets `rangeHint` ("must be between $0 and …").
function overrideFieldMessage(
  label: string,
  raw: string,
  problem: OverrideProblem,
  rangeHint: string,
): string {
  return problem === "malformed"
    ? `${label}: “${raw.trim()}” isn’t a valid number — enter a plain amount like 12.50, or leave it blank.`
    : `${label}: ${rangeHint}`;
}

// Per-application auth-fee override bounds. Mirrors the server-side sanity
// cap in pricing_tiers.coerce_auth_fee_override (auth fees are cents, not
// dollars); values outside this are rejected before submit.
const AUTH_FEE_OVERRIDE_MAX = 10;

// Parse the operator's auth-fee override input. "" (blank) means no override
// and returns undefined so the CreateApplicationInput omits the field and the
// backend stays on pure tiered pricing. A non-blank value returns its numeric
// form (which may be out of range — validated separately before submit).
export function parseAuthFeeOverride(raw: string): number | undefined {
  // Drop a "$" the operator may have typed (the input allows it for
  // familiarity) before parsing -- see feeInputSafe / stripAmountFormatting.
  const t = stripAmountFormatting(raw);
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

// Why the auth-fee override can't be boarded (malformed / out of range), or
// null when it's blank or valid. Blank is valid (no override).
export function authFeeOverrideProblem(raw: string): OverrideProblem | null {
  return classifyOverride(raw, { max: AUTH_FEE_OVERRIDE_MAX });
}

// True when the override input holds a value that cannot be boarded. Shared by
// both forms' validation and by buildInput's "send the override or null?" gate.
export function authFeeOverrideInvalid(raw: string): boolean {
  return authFeeOverrideProblem(raw) !== null;
}

// Operator-facing message for a bad auth-fee override -- see
// overrideFieldMessage. Callers guard on authFeeOverrideProblem first.
export function authFeeOverrideErrorMessage(raw: string): string {
  return overrideFieldMessage(
    "Authorization fee override",
    raw,
    authFeeOverrideProblem(raw) ?? "range",
    `must be between $0 and $${AUTH_FEE_OVERRIDE_MAX.toFixed(2)}, or left blank for standard pricing.`,
  );
}

// ----- Manual fee adjustment (admin-only, all fees on top of the tier) -----
//
// Companion to the auth-fee-only override above but covering every OTHER
// field on a tier's fees block (see lib/pricingTiers.ts FEE_OVERRIDE_FIELDS,
// the single source of truth for which fields exist and what widget each
// needs). authFee keeps the dedicated authFeeOverride state/field above for
// backward compatibility with its existing wiring; this form-local state
// covers the rest. All-blank submits `undefined` so CreateApplicationInput
// omits feeOverrides entirely and the backend stays on pure tiered pricing.

// Same sanity cap as pricing_tiers.py's _FEE_OVERRIDE_MAX -- generous
// compared to the auth-fee cap because fields like earlyCancelFee and
// annualMembershipFee are legitimately in the hundreds of dollars. This
// just blocks an obvious fat-fingered extra digit, not real pricing.
const FEE_OVERRIDE_MAX = 100_000;

// ----- Annual security bundle fee (admin-only, persisted to our DB only) ---
//
// CardConnect's CoPilot site has this input but the boarding API has no
// equivalent field, so the value is stored on the row (AnnualSecurityBundleFee)
// and NEVER sent to CardConnect -- it only feeds the pricing summary / fee
// schedule. Single scalar, $199 tier default. Same shape as the auth-fee
// override quartet above, validated against the generous FEE_OVERRIDE_MAX
// (it's a yearly charge in the hundreds, not a per-swipe cost).

// Parse the operator's Annual security bundle fee input. "" (blank) -> undefined
// so CreateApplicationInput omits the field and display falls back to the $199
// tier default. A non-blank value returns its numeric form (range-checked
// separately before submit).
export function parseAnnualSecurityBundleFee(raw: string): number | undefined {
  const t = stripAmountFormatting(raw);
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

// Why the Annual security bundle fee can't be saved (malformed / out of range),
// or null when it's blank or valid.
export function annualSecurityBundleFeeProblem(
  raw: string,
): OverrideProblem | null {
  return classifyOverride(raw, { max: FEE_OVERRIDE_MAX });
}

// True when the input holds a value that cannot be saved. Shared by validation
// and by buildInput's "send the value or omit it?" gate.
export function annualSecurityBundleFeeInvalid(raw: string): boolean {
  return annualSecurityBundleFeeProblem(raw) !== null;
}

// Operator-facing message for a bad Annual security bundle fee. Callers guard on
// annualSecurityBundleFeeProblem first.
export function annualSecurityBundleFeeErrorMessage(raw: string): string {
  return overrideFieldMessage(
    "Annual security bundle fee",
    raw,
    annualSecurityBundleFeeProblem(raw) ?? "range",
    `must be between $0 and $${FEE_OVERRIDE_MAX.toLocaleString()}, or blank for the $199 default.`,
  );
}

// stripAmountFormatting first so a "$"/"%" the operator typed (the input allows
// it) doesn't read as malformed. See classifyOverride for the malformed-vs-
// range split that drives the messages.
export function feeOverrideNumberInvalid(raw: string): boolean {
  return classifyOverride(raw, { max: FEE_OVERRIDE_MAX }) !== null;
}

// {field id, message} for every currency / currencyString override holding a
// bad value -- id matches each row's id="fee-override.<key>" /
// "send-fee-override.<key>". pciProgram and flag fields are <select>s
// constrained to valid options, so they need no range validation.
export function feeOverridesInvalidFields(
  form: FeeOverridesForm,
  idPrefix: string,
): { field: string; message: string }[] {
  const invalid: { field: string; message: string }[] = [];
  for (const field of FEE_OVERRIDE_FIELDS) {
    if (field.key === "authFee") continue; // own dedicated field/validator
    if (field.kind !== "currency" && field.kind !== "currencyString") continue;
    const key = field.key as keyof FeeOverridesForm;
    const problem = classifyOverride(form[key], { max: FEE_OVERRIDE_MAX });
    if (problem)
      invalid.push({
        field: `${idPrefix}.${key}`,
        message: overrideFieldMessage(
          field.label,
          form[key],
          problem,
          `must be between $0 and $${FEE_OVERRIDE_MAX.toLocaleString()}, or blank for the tier default.`,
        ),
      });
  }
  return invalid;
}

// {field id, message} when the merchant's state caps the ETF and the effective
// earlyCancelFee exceeds it, else null. UX parity with the server's ETF-cap
// guard (pricing_tiers.etf_cap_error / cardConnectStartHandler): surface the
// block before submit rather than as a CardConnect 409 (code 9872). The
// effective ETF is the operator override when one was set, else the tier
// default (every tier defaults to 750). idPrefix matches the fee-override row
// id (e.g. "fee-override.earlyCancelFee") so the error lands on the field.
export function earlyCancelFeeCapInvalidField(
  feeOverrides: FeeOverridesForm,
  stateCd: string,
  monthlyVolume: string,
  idPrefix: string,
): { field: string; message: string } | null {
  const overrideRaw = stripAmountFormatting(feeOverrides.earlyCancelFee);
  let effective: number | null;
  if (overrideRaw) {
    const n = Number(overrideRaw);
    // A malformed override is reported by feeOverridesInvalidFields; skip the
    // cap check here so the operator sees the parse error first.
    effective = Number.isFinite(n) ? n : null;
  } else {
    // Fall back to the lowest tier so the default (750) is always available
    // even when the volume can't resolve a tier -- the ETF default is the
    // same across every tier.
    const tier = selectPricingTier(monthlyVolume) ?? PRICING_TIERS[0];
    effective = tier.fees.earlyCancelFee;
  }
  const message = etfCapError(stateCd, effective);
  return message ? { field: `${idPrefix}.earlyCancelFee`, message } : null;
}

// Build the FeeOverridesInput wire payload, or undefined when every field
// is blank/invalid (so the key is omitted rather than sent as {}).
export function buildFeeOverridesInput(
  form: FeeOverridesForm,
): FeeOverridesInput | undefined {
  const out: Record<string, string | number | boolean> = {};
  for (const field of FEE_OVERRIDE_FIELDS) {
    if (field.key === "authFee") continue;
    const key = field.key as keyof FeeOverridesForm;
    const raw = form[key].trim();
    if (!raw) continue;
    switch (field.kind) {
      case "currency": {
        // Drop a "$" the operator may have typed (the input allows it) so
        // Number() sees a bare amount.
        const clean = stripAmountFormatting(raw);
        if (!clean) break;
        const n = Number(clean);
        if (Number.isFinite(n) && n >= 0 && n <= FEE_OVERRIDE_MAX) out[key] = n;
        break;
      }
      case "currencyString": {
        const clean = stripAmountFormatting(raw);
        if (!clean || feeOverrideNumberInvalid(clean)) break;
        out[key] = clean;
        break;
      }
      case "pciProgram":
        if (raw === "CONCIERGE" || raw === "ANNUAL") out.pciProgramCd = raw;
        break;
      case "flag":
        if (raw === "true" || raw === "false") out[key] = raw === "true";
        break;
    }
  }
  return Object.keys(out).length > 0 ? (out as FeeOverridesInput) : undefined;
}

// ----- Interchange-plus pricing overrides (admin-only) ---------------------
//
// Same "Manual fee adjustment" admin section, covering the icPlusPricing
// block (interchangeTypeCode + the four qualified-discount percentages)
// sharedModules.build_merchant_payload sends alongside the merchant. The
// backend's volume-derived defaults come from the hardcoded SSTHUB rate
// templates (sstshub_rate_templates.py), which these overrides layer on
// top of without altering.

// The four percentage fields are percent-as-number values (0.15 == 0.15%);
// real markups run well under 1%, so this just blocks a fat-fingered entry.
// Mirrors sstshub_rate_templates.py's _IC_PLUS_PRICING_PCT_MAX.
const IC_PLUS_PRICING_PCT_MAX = 100;

const IC_PLUS_PRICING_PCT_FIELDS = [
  "amexOptBlueQualDiscountPct",
  "discoverQualCreditDiscountPct",
  "mastercardQualCreditDiscountPct",
  "visaQualCreditDiscountPct",
] as const;

// Field labels for validation messages -- kept in step with the <TF label=…>
// text in ManualFeeAdjustmentSection's "Card percentage pricing" block.
const IC_PLUS_PRICING_PCT_LABELS: Record<
  (typeof IC_PLUS_PRICING_PCT_FIELDS)[number],
  string
> = {
  amexOptBlueQualDiscountPct: "Amex OptBlue qual. discount %",
  discoverQualCreditDiscountPct: "Discover qual. credit discount %",
  mastercardQualCreditDiscountPct: "Mastercard qual. credit discount %",
  visaQualCreditDiscountPct: "Visa qual. credit discount %",
};

export function icPlusPricingOverridesInvalidFields(
  form: IcPlusPricingOverridesForm,
  idPrefix: string,
): { field: string; message: string }[] {
  const invalid: { field: string; message: string }[] = [];
  for (const key of IC_PLUS_PRICING_PCT_FIELDS) {
    const problem = classifyOverride(form[key], {
      max: IC_PLUS_PRICING_PCT_MAX,
    });
    if (problem)
      invalid.push({
        field: `${idPrefix}.${key}`,
        message: overrideFieldMessage(
          IC_PLUS_PRICING_PCT_LABELS[key],
          form[key],
          problem,
          `must be a percentage between 0 and ${IC_PLUS_PRICING_PCT_MAX}, or blank for the template default.`,
        ),
      });
  }
  return invalid;
}

export function buildIcPlusPricingOverridesInput(
  form: IcPlusPricingOverridesForm,
): IcPlusPricingOverridesInput | undefined {
  const out: Record<string, string | number> = {};
  const code = form.interchangeTypeCode.trim().toUpperCase();
  if (code === "GROSS" || code === "NET") out.interchangeTypeCode = code;
  for (const key of IC_PLUS_PRICING_PCT_FIELDS) {
    const raw = stripAmountFormatting(form[key]);
    if (!raw) continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0 && n <= IC_PLUS_PRICING_PCT_MAX)
      out[key] = n;
  }
  return Object.keys(out).length > 0
    ? (out as IcPlusPricingOverridesInput)
    : undefined;
}

// Admin fleet-pricing overrides. DELIBERATELY a SEPARATE parallel mechanism to
// the fee overrides above (see lib/pricingTiers.ts FLEET_OVERRIDE_FIELDS, the
// single source of truth for the admin-adjustable fleet fields). Unlike
// feeOverrides, fleet overrides are validated + persisted for future
// MAR/display use only and NEVER reach the CardConnect payload. All-blank
// submits `undefined` so CreateApplicationInput omits fleetOverrides entirely.

// {field id, message} for every fleet override holding a bad value -- id
// matches each row's id="fleet-override.<key>" / "send-fleet-override.<key>".
// currency/percent fields share FEE_OVERRIDE_MAX (same sanity cap as the manual
// fee overrides); the "number" field (ebtFnsNumber) is a USDA FNS identifier
// validated as an exact 7-digit STRING -- leading/trailing zeros are
// significant, so it is never parsed as a number (issue #197).
export function fleetOverridesInvalidFields(
  form: FleetOverridesForm,
  idPrefix: string,
): { field: string; message: string }[] {
  const invalid: { field: string; message: string }[] = [];
  for (const field of FLEET_OVERRIDE_FIELDS) {
    const key = field.key as keyof FleetOverridesForm;
    if (field.kind === "number") {
      // FNS number: blank means "no override"; a non-blank value must be
      // exactly 7 digits (leading zeros count).
      const raw = (form[key] ?? "").trim();
      if (raw && !isValidFnsNumber(raw)) {
        invalid.push({
          field: `${idPrefix}.${key}`,
          message: `${field.label}: must be exactly ${FNS_NUMBER_DIGITS} digits (leading zeros count), or blank.`,
        });
      }
      continue;
    }
    const problem = classifyOverride(form[key], { max: FEE_OVERRIDE_MAX });
    if (problem)
      invalid.push({
        field: `${idPrefix}.${key}`,
        message: overrideFieldMessage(
          field.label,
          form[key],
          problem,
          `must be between $0 and $${FEE_OVERRIDE_MAX.toLocaleString()}, or blank for the tier default.`,
        ),
      });
  }
  return invalid;
}

// Build the FleetOverridesInput wire payload, or undefined when every field
// is blank/invalid (so the key is omitted rather than sent as {}).
export function buildFleetOverridesInput(
  form: FleetOverridesForm,
): FleetOverridesInput | undefined {
  const out: Record<string, number | string> = {};
  for (const field of FLEET_OVERRIDE_FIELDS) {
    const key = field.key as keyof FleetOverridesForm;
    if (field.kind === "number") {
      // FNS number: send the exact 7-digit string (leading/trailing zeros are
      // significant), never Number() it (issue #197).
      const raw = (form[key] ?? "").trim();
      if (isValidFnsNumber(raw)) out[key] = raw;
      continue;
    }
    // Drop a "$"/"%" the operator may have typed (the currency + percent
    // inputs allow the matching symbol) so Number() sees a bare value.
    const raw = stripAmountFormatting(form[key]);
    if (!raw) continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0 && n <= FEE_OVERRIDE_MAX) {
      out[key] = n;
    }
  }
  return Object.keys(out).length > 0 ? (out as FleetOverridesInput) : undefined;
}

// ----- Clover Security + TransArmor overrides (admin-only) -----------------
//
// A THIRD parallel object to feeOverrides/icPlusPricingOverrides (see
// lib/pricingTiers.ts CLOVER_SECURITY_AND_TRANSARMOR_FIELDS, the single
// source of truth for which fields exist and what widget each needs).
// Unlike fleetOverrides, this DOES reach the CardConnect payload
// (merchant.cloverSecurityAndTransarmor). All-blank submits `undefined` so
// CreateApplicationInput omits the key entirely and the tier default block
// applies untouched.

// Mirrors pricing_tiers.py's _TRANSARMOR_DEPENDENT_FIELDS /
// _CLOVER_SECURITY_DEPENDENT_FIELDS / finalize_clover_security_and_
// transarmor -- the authoritative gate the backend applies right before the
// CardConnect submit. A field in one of these lists only takes effect when
// its product's "enable" field is on:
//   - TransArmor      <-> transarmorDataProtectionFlg is truthy.
//   - Clover Security <-> cloverSecurityBundleCd is a non-empty string.
// When the enable field is OFF, finalize_clover_security_and_transarmor
// silently DROPS the dependent fields before the CardConnect payload is
// built -- they're never even sent, so CardConnect never gets a chance to
// reject them. Without this check, an operator who sets e.g.
// transarmorMultiPayToken without turning on TransArmor Data Protection
// gets no error at all; their override is just discarded at submit,
// invisibly. That silent-discard case (reported 2026-08-05) is the actual
// bug this list closes -- catching it here surfaces it as a blocking
// validation error instead.
const TRANSARMOR_DEPENDENT_FIELDS = [
  "transarmorTokenTypeCd",
  "transarmorMultiPayToken",
  "transarmorMonthlyFee",
] as const;
const CLOVER_SECURITY_DEPENDENT_FIELDS = [
  "cloverSecurityPlusFee",
  "pciComplianceServiceFee",
  "pciComplianceServiceFeeFrequency",
  "pciComplianceServiceFeeMonthToBill",
] as const;

// BUG (reported 2026-08-27): a dependent field's raw string was treated as
// "an override was attempted" via plain `.trim()` truthiness -- so a literal
// "0" (a non-empty string) blocked submission with "Only allowed when
// TransArmor data protection is enabled" / "...a Clover Security bundle code
// is selected", even though 0 is the tier default for every currency/number
// field here (CLOVER_SECURITY_AND_TRANSARMOR_DEFAULTS) and CardConnect's own
// rule (see transarmorMonthlyFee's enabled-case handling below) already
// treats a present "0" as a normal value, not an override attempt. The only
// workaround was blanking the field. Fixed by treating a currency/number
// field as blank whenever it's empty OR parses to 0 -- select/string fields
// (token type code, multi-pay token, month-to-bill) keep the stricter
// any-non-blank-is-an-attempt check, since there's no zero-equivalent
// sentinel for those.
function cloverSecurityFieldIsBlank(
  key: keyof CloverSecurityAndTransarmorForm,
  raw: string,
): boolean {
  const t = raw.trim();
  if (!t) return true;
  const kind = CLOVER_SECURITY_AND_TRANSARMOR_FIELDS.find(
    (f) => f.key === key,
  )?.kind;
  if (kind !== "currency" && kind !== "number") return false;
  // Strip formatting so a lone "$" the operator typed reads as blank, and
  // "$0" reads as the zero sentinel.
  const clean = stripAmountFormatting(t);
  if (!clean) return true;
  const n = Number(clean);
  return Number.isFinite(n) && n === 0;
}

// Cross-field TransArmor rules CardConnect additionally enforces server-side
// for the enabled case (confirmed via live 400s, not documentation): code
// 9742 "The TransArmor Token Type Code is required when TransArmor Data
// Protection is enabled." -- data protection ON, token type blank. Code
// 9738's "not allowed" half is the disabled-case rule above (folded into
// TRANSARMOR_DEPENDENT_FIELDS since transarmorTokenTypeCd is one of them);
// its "...or invalid" half is re-checked below as defense-in-depth for a
// legacy stored value outside {MULTI,SINGLE} -- the <select> widget already
// constrains new entries to that enum (see CloverSecurityAndTransarmor
// Section's showsUnknown). transarmorMonthlyFee gets the same
// required-when-enabled treatment as the token type (Susan, 2026-08-05) --
// CardConnect boards TransArmor fine with a $0.00 fee, but not with the fee
// omitted entirely.
//
// All enable flags resolve against CLOVER_SECURITY_AND_TRANSARMOR_DEFAULTS
// (every tier shares the same off/null baseline -- see that constant's
// docstring), not literal form blankness, so "operator left it blank"
// correctly reads as "off" rather than skipping the check.
function cloverSecurityAndTransarmorCrossFieldErrors(
  form: CloverSecurityAndTransarmorForm,
): { field: keyof CloverSecurityAndTransarmorForm; message: string }[] {
  const errors: {
    field: keyof CloverSecurityAndTransarmorForm;
    message: string;
  }[] = [];

  const dataProtectionOn =
    form.transarmorDataProtectionFlg === ""
      ? CLOVER_SECURITY_AND_TRANSARMOR_DEFAULTS.transarmorDataProtectionFlg
      : form.transarmorDataProtectionFlg === "true";
  const tokenType = form.transarmorTokenTypeCd.trim();

  if (!dataProtectionOn) {
    for (const field of TRANSARMOR_DEPENDENT_FIELDS) {
      if (!cloverSecurityFieldIsBlank(field, form[field])) {
        errors.push({
          field,
          message: "Only allowed when TransArmor data protection is enabled.",
        });
      }
    }
  } else {
    // Independent checks (not else-if chained) so an operator who left
    // BOTH the token type and the fee blank sees both errors at once,
    // rather than fixing one only to hit the other on the next submit.
    if (!tokenType) {
      errors.push({
        field: "transarmorTokenTypeCd",
        message: "Required when TransArmor data protection is enabled.",
      });
    } else if (
      !(TRANSARMOR_TOKEN_TYPE_CODES as readonly string[]).includes(tokenType)
    ) {
      errors.push({
        field: "transarmorTokenTypeCd",
        message: `Must be one of: ${TRANSARMOR_TOKEN_TYPE_CODES.join(", ")}.`,
      });
    }
    // CardConnect boards TransArmor with a $0.00 fee fine (a present "0"
    // passes) -- it's an omitted fee entirely that's the problem, same
    // "enabled but incomplete" shape as a missing token type.
    if (!stripAmountFormatting(form.transarmorMonthlyFee)) {
      errors.push({
        field: "transarmorMonthlyFee",
        message: "Required when TransArmor data protection is enabled.",
      });
    }
  }

  const bundleOn = form.cloverSecurityBundleCd.trim() !== "";
  if (!bundleOn) {
    for (const field of CLOVER_SECURITY_DEPENDENT_FIELDS) {
      if (!cloverSecurityFieldIsBlank(field, form[field])) {
        errors.push({
          field,
          message:
            "Only allowed when a Clover Security bundle code is selected.",
        });
      }
    }
  }

  return errors;
}

// Dotted field ids (matching each row's id="clover-security.<key>" /
// "send-clover-security.<key>") that hold an invalid value, paired with the
// message to show for that field. Most flag/string fields are never invalid
// here on their own -- flag is a constrained <select>, and the remaining
// string fields (multi-pay token, bundle code, etc.) have no format
// constraint to violate -- but several are cross-field-dependent on their
// product's enable flag/bundle code, checked via
// cloverSecurityAndTransarmorCrossFieldErrors above.
export function cloverSecurityAndTransarmorInvalidFields(
  form: CloverSecurityAndTransarmorForm,
  idPrefix: string,
): { field: string; message: string }[] {
  const invalid: { field: string; message: string }[] = [];
  for (const field of CLOVER_SECURITY_AND_TRANSARMOR_FIELDS) {
    const key = field.key as keyof CloverSecurityAndTransarmorForm;

    // monthToBill is a month-name dropdown, but a legacy row may still carry a
    // non-month string ("0.00") kept selectable by the render -- flag it
    // rather than send it.
    if (key === "pciComplianceServiceFeeMonthToBill") {
      const raw = form[key].trim();
      if (raw && canonicalMonthToBill(raw) === null)
        invalid.push({
          field: `${idPrefix}.${key}`,
          message: `${field.label}: “${raw}” isn’t a valid month — pick a month, or leave it unset.`,
        });
      continue;
    }

    if (field.kind !== "currency" && field.kind !== "number") continue;
    const isWholeNumber = field.kind === "number";
    const problem = classifyOverride(form[key], {
      max: FEE_OVERRIDE_MAX,
      integer: isWholeNumber,
    });
    if (problem)
      invalid.push({
        field: `${idPrefix}.${key}`,
        message: overrideFieldMessage(
          field.label,
          form[key],
          problem,
          isWholeNumber
            ? `must be a whole number up to ${FEE_OVERRIDE_MAX.toLocaleString()}, or blank for the tier default.`
            : `must be between $0 and $${FEE_OVERRIDE_MAX.toLocaleString()}, or blank for the tier default.`,
        ),
      });
  }
  for (const { field, message } of cloverSecurityAndTransarmorCrossFieldErrors(
    form,
  )) {
    invalid.push({ field: `${idPrefix}.${field}`, message });
  }
  return invalid;
}

// Build the CloverSecurityAndTransarmorInput wire payload, or undefined
// when every field is blank/invalid (so the key is omitted rather than
// sent as {}).
export function buildCloverSecurityAndTransarmorInput(
  form: CloverSecurityAndTransarmorForm,
): CloverSecurityAndTransarmorInput | undefined {
  const out: Record<string, string | number | boolean> = {};
  for (const field of CLOVER_SECURITY_AND_TRANSARMOR_FIELDS) {
    const key = field.key as keyof CloverSecurityAndTransarmorForm;
    const raw = form[key].trim();
    if (!raw) continue;
    switch (field.kind) {
      case "currency": {
        // Drop a "$" the operator may have typed (the input allows it) so
        // Number() sees a bare amount.
        const clean = stripAmountFormatting(raw);
        if (!clean) break;
        const n = Number(clean);
        if (Number.isFinite(n) && n >= 0 && n <= FEE_OVERRIDE_MAX) out[key] = n;
        break;
      }
      case "number": {
        const clean = stripAmountFormatting(raw);
        if (!clean) break;
        const n = Number(clean);
        if (Number.isInteger(n) && n >= 0 && n <= FEE_OVERRIDE_MAX)
          out[key] = n;
        break;
      }
      case "flag":
        if (raw === "true" || raw === "false") out[key] = raw === "true";
        break;
      case "string":
        out[key] = raw;
        break;
      case "select":
        // monthToBill is a dropdown of month names now; normalize a legacy
        // stored value ("3" / "march") to its canonical name on the wire.
        // Validation blocks an unrecognized value before we get here, so the
        // `?? raw` is just a belt-and-braces passthrough.
        out[key] =
          key === "pciComplianceServiceFeeMonthToBill"
            ? (canonicalMonthToBill(raw) ?? raw)
            : raw;
        break;
    }
  }
  return Object.keys(out).length > 0
    ? (out as CloverSecurityAndTransarmorInput)
    : undefined;
}
